import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useCurrentUserRoleNames, usePermission } from "components/RequirePermission/useAccessPermissions";
import { isCrossOrgAdminRole } from "components/RequirePermission/RequireEcrOpsRole";
import { PERMISSIONS } from "utils/accessManagement/permissionKeys";
import { SECTION_LABELS } from "./useDashboardSettings";

const ACCENT = "#26890d";

// This component only ever renders on the ecr-ops enterprise Copilot
// dashboard (already gated by RequireEcrOpsRole to the same three roles —
// EnterpriseAdmin/OperationsAdmin/OperationsUser — see isCrossOrgAdminRole),
// never on the per-org admin/:orgId/copilot-dashboard page, so there's no
// per-org role to scope against here — a plain global usePermission check is
// correct (unlike the org-scoped useEffectiveOrgPermission machinery used
// elsewhere). isCrossOrgAdminRole is kept alongside the permission as
// defense-in-depth, same pattern as the rest of the ecr-ops surface.
// Visibility (can even see the gear icon) vs. editing (can actually change
// section visibility / the overage rate) are separate capabilities.
const useCanViewDashboardSettings = () => {
  const roleNames = useCurrentUserRoleNames();
  const hasPermission = usePermission(PERMISSIONS.COPILOT_SETTINGS_READ);
  return hasPermission && isCrossOrgAdminRole(roleNames);
};

const useCanEditDashboardSettings = () => {
  const roleNames = useCurrentUserRoleNames();
  const hasPermission = usePermission(PERMISSIONS.COPILOT_DASHBOARD_SETTING_UPDATE);
  return hasPermission && isCrossOrgAdminRole(roleNames);
};

const DashboardSettingsMenu = ({ visibleSections, onToggleSection, deloitteOverageRate, onUpdateDeloitteOverageRate, onReset }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rateInput, setRateInput] = useState(String(deloitteOverageRate ?? ""));
  const open = Boolean(anchorEl);
  const canView = useCanViewDashboardSettings();
  const canEdit = useCanEditDashboardSettings();

  // Gear icon itself is hidden entirely without user:copilot:settings:read.
  // Someone who can view but not edit (missing user:copilot:dashboard:setting:update)
  // still sees the icon and popover, just with every control disabled below.
  if (!canView) return null;

  const handleOpen = (e) => {
    setRateInput(String(deloitteOverageRate ?? ""));
    setAnchorEl(e.currentTarget);
  };

  const commitRate = () => {
    const parsed = Number.parseFloat(rateInput);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      onUpdateDeloitteOverageRate(parsed);
    } else {
      setRateInput(String(deloitteOverageRate ?? ""));
    }
  };

  return (
    <>
      <Tooltip title="Customize dashboard settings" placement="top" arrow>
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{
            color: open ? ACCENT : "#555",
            border: `1px solid ${open ? ACCENT : "#ccc"}`,
            borderRadius: "16px",
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: "#f2f8e6", borderColor: ACCENT, color: ACCENT },
          }}
        >
          <SettingsOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: "12px", border: "1px solid #e8e8e8", boxShadow: "0 12px 32px rgba(0,0,0,0.14)", mt: 1 } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1a1a1a" }}>
              Dashboard Settings
            </Typography>
            {canEdit && (
              <Typography
                onClick={onReset}
                sx={{
                  fontSize: "0.72rem",
                  color: "#888",
                  cursor: "pointer",
                  "&:hover": { color: ACCENT, textDecoration: "underline" },
                }}
              >
                Reset
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: "0.72rem", color: "#999", mb: 1.5 }}>
            {canEdit
              ? "Applies to everyone. Choose which sections appear on this dashboard — changes apply immediately."
              : "Applies to everyone. You have view-only access to these settings."}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {Object.entries(SECTION_LABELS).map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={visibleSections[key] ?? true}
                    onChange={() => onToggleSection(key)}
                    disabled={!canEdit}
                    size="small"
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": { color: ACCENT },
                      py: 0.4,
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: "0.82rem", color: "#333" }}>{label}</Typography>}
                sx={{ ml: 0, mr: 0 }}
              />
            ))}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#555", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Deloitte Overage Rate
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "#999", mb: 1 }}>
            Negotiated $/AIC rate applied to overage billing across the dashboard.
          </Typography>
          <TextField
            value={rateInput}
            onChange={(e) => setRateInput(e.target.value)}
            onBlur={commitRate}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            size="small"
            type="number"
            disabled={!canEdit}
            inputProps={{ step: "0.0001", min: 0 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: <InputAdornment position="end">/AIC</InputAdornment>,
            }}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.82rem",
                "&.Mui-focused fieldset": { borderColor: ACCENT },
              },
            }}
          />
        </Box>
      </Popover>
    </>
  );
};

export default DashboardSettingsMenu;
